import React, { useState, useEffect, useRef, useCallback } from 'react';
import { List, ListImperativeAPI } from 'react-window';
import { EdgeDocsPageItem } from './EdgeDocsPageItem';
import { DPIPersistenceService } from '../../core/utils/DPIPersistenceService';
import { PAGE_SIZES } from '../../core/types/Typesetter';
import { TypesetterWorkerClient } from '../../workers/TypesetterWorkerClient';
import { PageSetupConverter } from '../../utils/PageSetupConverter';
import { SharedInputProvider, useSharedInput } from './SharedInputContext';
import { ScrollPageIndicator } from './ScrollPageIndicator';
import { CacheStatusIndicator } from './CacheStatusIndicator';
import { useKeyboardScrolling } from './useKeyboardScrolling';

// Debug flag - set to false for production
const DEBUG_ENABLED = false;

/**
 * 🆕 Helper component to register paragraph getter with parent
 * Must be inside SharedInputProvider to access context
 */
const ParagraphGetterRegistrar: React.FC<{
  onReady?: (getter: () => Array<{
    id: string;
    type: string;
    version: number;
    textStart: number;
    textEnd: number;
  }>) => void;
}> = ({ onReady }) => {
  const sharedInput = useSharedInput();
  
  useEffect(() => {
    if (onReady && sharedInput?.getDocumentParagraphs) {
      DEBUG_ENABLED && console.log('📊 [ParagraphGetterRegistrar] Registering paragraph getter');
      onReady(sharedInput.getDocumentParagraphs);
    }
  }, [onReady, sharedInput?.getDocumentParagraphs]);
  
  return null; // This component doesn't render anything
};

/**
 * 🆕 Helper component to register undo/redo handlers with parent
 * Must be inside SharedInputProvider to access context
 */
const UndoRedoRegistrar: React.FC<{
  onReady?: (handlers: {
    undo: () => Promise<void>;
    redo: () => Promise<void>;
    canUndo: boolean;
    canRedo: boolean;
  }) => void;
}> = ({ onReady }) => {
  const sharedInput = useSharedInput();
  
  useEffect(() => {
    if (onReady && sharedInput) {
      DEBUG_ENABLED && console.log('↩️ [UndoRedoRegistrar] Registering undo/redo handlers');
      onReady({
        undo: sharedInput.undo,
        redo: sharedInput.redo,
        canUndo: sharedInput.canUndo,
        canRedo: sharedInput.canRedo
      });
    }
  }, [onReady, sharedInput?.undo, sharedInput?.redo, sharedInput?.canUndo, sharedInput?.canRedo]);
  
  return null; // This component doesn't render anything
};

/**
 * EdgeDocs Virtual Pages V3 - Multi-page container
 *
 * Renders multiple pages vertically with proper spacing.
 * WASM handles pagination, this component just displays the pages.
 */
interface EdgeDocsVirtualPagesV3Props {
  /** Document text and metadata */
  documentState?: { text: string; pageSetup?: any; fontSettings?: any };
  /** Document ID for persistent caching (matches Postgres document.id) */
  docId?: string;
  /** Version ID for persistent caching (matches Postgres document_version.id) */
  versionId?: string;
  /** 🆕 Server-computed AST hash for cache invalidation (authoritative, from API response) */
  astHash?: string;
  /** Page width in pixels */
  pageWidth?: number;
  /** Page height in pixels */
  pageHeight?: number;
  /** Zoom mode: 'fit' | 'fit-width' | 'custom' */
  zoomMode?: 'fit' | 'fit-width' | 'custom';
  /** User zoom value (renderer multiplies by device scale) */
  userZoom?: number;
  /** CSS class name */
  className?: string;
  /** Callback for WASM initialization status changes */
  onWasmStatusChange?: (status: 'loading' | 'ready' | 'error') => void;
  /** Callback for document text changes from input system */
  onDocumentTextChange?: (newText: string) => void;
  /** Callback for page count changes from WASM */
  onPageCountChange?: (pageCount: number) => void;
  /** 🆕 Callback to register paragraph getter for save operations */
  onParagraphGetterReady?: (getter: () => Array<{
    id: string;
    type: string;
    version: number;
    textStart: number;
    textEnd: number;
  }>) => void;
  /** 🆕 Callback to register undo/redo handlers for toolbar */
  onUndoRedoReady?: (handlers: {
    undo: () => Promise<void>;
    redo: () => Promise<void>;
    canUndo: boolean;
    canRedo: boolean;
  }) => void;
}

export const EdgeDocsVirtualPagesV3: React.FC<EdgeDocsVirtualPagesV3Props> = ({
  documentState,
  docId,
  versionId,
  astHash,  // 🆕 Server-computed AST hash
  pageWidth: providedPageWidth,
  pageHeight: providedPageHeight,
  zoomMode = 'fit',
  userZoom = 1,
  className = '',
  onWasmStatusChange,
  onDocumentTextChange,
  onPageCountChange,
  onParagraphGetterReady,
  onUndoRedoReady
}) => {
  // 🔬 DEBUG: Track instance identity to prove mount/unmount vs effect churn
  const instanceId = React.useRef(crypto.randomUUID().slice(0, 8));
  React.useEffect(() => {
    console.log(`🔬 [EdgeDocsVirtualPagesV3] MOUNTED instance=${instanceId.current}`);
    return () => {
      console.log(`🔬 [EdgeDocsVirtualPagesV3] UNMOUNTED instance=${instanceId.current}`);
    };
  }, []);

  // ✅ CRITICAL FIX: Memoize page setup conversion to prevent unnecessary recalculations
  const documentPageSetup = React.useMemo(() => {
    return documentState?.pageSetup
      ? PageSetupConverter.toWasmFormat(documentState.pageSetup)
      : PAGE_SIZES.LETTER;
  }, [documentState?.pageSetup]);

  // Use provided dimensions or calculate from document page setup
  const pageWidth = providedPageWidth ||
    ((documentPageSetup.content_rect_fp.w_fp + documentPageSetup.margins_fp.left + documentPageSetup.margins_fp.right) / 64); // Convert from fixed-point

  const pageHeight = providedPageHeight ||
    ((documentPageSetup.content_rect_fp.h_fp + documentPageSetup.margins_fp.top + documentPageSetup.margins_fp.bottom) / 64); // Convert from fixed-point
  const listRef = useRef<ListImperativeAPI | null>(null);
  
  // PHASE 2.2: Shared WASM worker for all pages
  const sharedWasmClientRef = useRef<TypesetterWorkerClient | null>(null);
  const [isSharedWorkerReady, setIsSharedWorkerReady] = useState(false);
  const [paginationResult, setPaginationResult] = useState<any>(null);
  // Track previous text to detect empty → non-empty transition
  const previousTextRef = useRef<string>('');
  // ✅ FONT SWITCHING FIX: Track previous font settings to detect font changes
  const previousFontSettingsRef = useRef<any>(null);
  
  // Calculate final scale: deviceScale * userZoom (special handling for 'fit' mode)
  const deviceScale = DPIPersistenceService.getDeviceScale(); // = DPI / 96
  const finalScale =
    zoomMode === 'fit' ? deviceScale
                       : deviceScale * userZoom; // fit-width/custom
  
  // Track actual page count from WASM
  const [actualPageCount, setActualPageCount] = useState(1);
  
  // Track which pages have been rendered (to prevent duplicate WASM calls)
  const renderedPagesRef = useRef<Set<number>>(new Set());
  
  // 🆕 Cache status state for UI indicator
  const [cacheStatus, setCacheStatus] = useState<{
    enabled: boolean;
    loading: boolean;
    source: 'cache' | 'typeset' | null;
    hitCount: number;
    missCount: number;
  }>({ enabled: false, loading: false, source: null, hitCount: 0, missCount: 0 });
  
  // Estimate page count from text length as fallback
  const estimatedPageCount = Math.max(1, Math.ceil((documentState?.text?.length || 0) / 3000));
  const pageCount = Math.max(actualPageCount, estimatedPageCount);
  const pageGap = 20; // Gap between pages in pixels
  
  DEBUG_ENABLED && console.log(`📄 [VirtualPagesV3] Rendering ${pageCount} pages (${actualPageCount} actual, ${estimatedPageCount} estimated):`, {
    textLength: documentState?.text?.length || 0,
    pageWidth: pageWidth.toFixed(1),
    pageHeight: pageHeight.toFixed(1),
    pageSetup: documentState?.pageSetup ? 'custom' : 'default',
    finalScale: finalScale.toFixed(3)
  });
  
  // PHASE 2.2: Initialize shared WASM worker
  useEffect(() => {
    const initSharedWorker = async () => {
      DEBUG_ENABLED && console.log('🚀 [VirtualPagesV3] Initializing shared WASM worker...');
      onWasmStatusChange?.('loading');
      
      try {
        const client = new TypesetterWorkerClient({
          onReady: () => {
            DEBUG_ENABLED && console.log('✅ [VirtualPagesV3] Shared WASM worker ready');
            setIsSharedWorkerReady(true);
            onWasmStatusChange?.('ready');
          },
          onError: (error) => {
            console.error('❌ [VirtualPagesV3] Shared WASM worker error:', error);
            onWasmStatusChange?.('error');
          }
        });
        
        await client.initialize();
        sharedWasmClientRef.current = client;
        DEBUG_ENABLED && console.log('✅ [VirtualPagesV3] Shared WASM worker initialized');
        
        // 🆕 Enable persistent cache if docId is available
        if (docId) {
          // Use docId:versionId as cache key for version-specific caching
          const cacheKey = versionId ? `${docId}:${versionId}` : docId;
          await client.enablePersistentCache(cacheKey);
          setCacheStatus(prev => ({ ...prev, enabled: true }));
          console.log(`💾 [VirtualPagesV3] Persistent cache enabled for: ${cacheKey}`);
        }
        
      } catch (error) {
        console.error('❌ [VirtualPagesV3] Failed to initialize shared WASM worker:', error);
        onWasmStatusChange?.('error');
      }
    };
    
    initSharedWorker();
    
    return () => {
      DEBUG_ENABLED && console.log('🧹 [VirtualPagesV3] Cleaning up shared WASM worker');
      if (sharedWasmClientRef.current) {
        sharedWasmClientRef.current.terminate();
        sharedWasmClientRef.current = null;
      }
    };
  }, [docId, versionId]); // Re-init if docId or versionId changes (enables cache for that version)
  
  // PHASE 2.2: Initialize document and paginate entire document once, cache result
  useEffect(() => {
    const initializeAndPaginate = async () => {
      // 🔬 ALWAYS-ON: Track useEffect trigger
      console.log('🔄 [VirtualPagesV3] initializeAndPaginate useEffect triggered:', {
        isSharedWorkerReady,
        hasClient: !!sharedWasmClientRef.current,
        textLength: documentState?.text?.length,
        paragraphCount: (documentState as any)?.paragraphs?.length || 0
      });
      
      // ✅ FIX: Allow initialization with empty text (empty string is valid for new documents)
      // Check for undefined/null, not falsy value (empty string '' should pass)
      if (!isSharedWorkerReady || !sharedWasmClientRef.current || documentState?.text === undefined || documentState?.text === null) {
        console.log('⏭️ [VirtualPagesV3] Skipping init - not ready or no documentState:', {
          isSharedWorkerReady,
          hasClient: !!sharedWasmClientRef.current,
          textValue: documentState?.text?.substring(0, 50),
          textIsUndefined: documentState?.text === undefined,
          textIsNull: documentState?.text === null,
          paragraphCount: (documentState as any)?.paragraphs?.length || 0
        });
        return;
      }
      
      // 🔧 CRITICAL FIX: Wait for paragraphs to be loaded before initializing WASM
      // If document has text but no paragraphs, it means AST extraction hasn't completed yet.
      // Without this check, WASM initializes with a single monolithic paragraph, causing
      // incorrect rendering (all \n treated as line breaks instead of \n\n as paragraph delimiters).
      const hasParagraphs = (documentState as any)?.paragraphs?.length > 0;
      const hasText = documentState.text.length > 0;
      if (hasText && !hasParagraphs) {
        console.log('⏳ [VirtualPagesV3] Waiting for paragraphs - text loaded but paragraphs not yet available:', {
          textLength: documentState.text.length,
          paragraphCount: 0,
          textPreview: documentState.text.substring(0, 50)
        });
        return;
      }
      
      const currentText = documentState.text;
      const previousText = previousTextRef.current;
      const currentFontSettings = documentState.fontSettings;
      const previousFontSettings = previousFontSettingsRef.current;
      
      // ✅ FIX: Detect empty → non-empty transition
      const isEmptyToNonEmpty = previousText.length === 0 && currentText.length > 0;
      
      // ✅ FONT SWITCHING FIX: Detect font settings change
      // Compare font settings values (not object references) to detect actual changes
      const fontSettingsChanged = previousFontSettings && currentFontSettings && (
        previousFontSettings.fontFamily !== currentFontSettings.fontFamily ||
        previousFontSettings.fontWeight !== currentFontSettings.fontWeight ||
        previousFontSettings.fontStyle !== currentFontSettings.fontStyle ||
        previousFontSettings.fontSizePx !== currentFontSettings.fontSizePx ||
        previousFontSettings.lineHeightMul !== currentFontSettings.lineHeightMul
      );
      
      // ✅ FIX: Skip if text is empty and we've already initialized (don't re-init with empty)
      // paginationResult exists means we've already initialized
      // Exception: allow re-init if font settings changed
      if (currentText.length === 0 && paginationResult && !isEmptyToNonEmpty && !fontSettingsChanged) {
        DEBUG_ENABLED && console.log('⏭️ [VirtualPagesV3] Skipping initialization with empty text (already initialized)');
        return;
      }
      
      // Update previous refs BEFORE initialization
      previousTextRef.current = currentText;
      previousFontSettingsRef.current = currentFontSettings;
      
      // ✅ FIX: If transitioning from empty to non-empty, force re-initialization
      if (isEmptyToNonEmpty) {
        DEBUG_ENABLED && console.log('🔄 [VirtualPagesV3] Detected empty → non-empty transition, forcing re-initialization', {
          previousLength: previousText.length,
          currentLength: currentText.length
        });
      }
      
      // ✅ FONT SWITCHING FIX: If font settings changed, force re-initialization
      // ⚠️ CRITICAL: Don't re-initialize if text exists - this causes text to disappear
      // Font changes should only affect NEW text or SELECTED text, not entire document
      // TODO: Implement selection-based font application (like MS Word)
      if (fontSettingsChanged) {
        DEBUG_ENABLED && console.log('🎨 [VirtualPagesV3] Font settings changed', {
          previous: previousFontSettings,
          current: currentFontSettings,
          textLength: currentText.length,
          textPreserved: currentText.length > 0
        });
        
        // ✅ CRITICAL FIX: Don't re-initialize if document has text
        // Font changes should only affect new text or selected text
        // Re-initialization clears the document and causes text to disappear
        if (currentText.length > 0) {
          DEBUG_ENABLED && console.warn('⚠️ [VirtualPagesV3] Font changed but document has text - skipping re-init to preserve text');
          DEBUG_ENABLED && console.warn('⚠️ [VirtualPagesV3] Font will only apply to NEW text typed after this change');
          DEBUG_ENABLED && console.warn('⚠️ [VirtualPagesV3] TODO: Implement selection-based font application');
          // Don't trigger re-initialization - just update the font for future text
          return;
        }
        
        // Only re-initialize if document is empty (new document)
        DEBUG_ENABLED && console.log('🎨 [VirtualPagesV3] Font changed on empty document - safe to re-initialize');
      }
      
      DEBUG_ENABLED && console.log('📊 [VirtualPagesV3] Initializing document and paginating...', {
        textLength: currentText.length,
        isEmptyToNonEmpty,
        fontSettingsChanged,
        fontFamily: currentFontSettings?.fontFamily,
        fontSizePx: currentFontSettings?.fontSizePx
      });
      
      try {
        // Step 1: Initialize document (required before pagination)
        // Convert page setup to WASM-compatible format using consolidated converter
        const rawPageSetup = documentState.pageSetup
          ? PageSetupConverter.toWasmFormat(documentState.pageSetup)
          : PAGE_SIZES.LETTER;

        const pageSetup = PageSetupConverter.toInitializeDocumentFormat(rawPageSetup);

        // Ensure pageSize is always an object format for WASM compatibility
        const wasmCompatiblePageSetup = {
          ...pageSetup,
          pageSize: typeof pageSetup.pageSize === 'string'
            ? { width: 8.5, height: 11 } // Fallback to Letter if somehow string
            : pageSetup.pageSize
        };
        
        // ✅ FONT SWITCHING FIX: Get font settings from effective font settings (toolbar props override document metadata)
        const fontSettings = (documentState as any)?.fontSettings || {};
        const fontFamily = typeof fontSettings.fontFamily === 'string' 
          ? fontSettings.fontFamily.replace(/'/g, '') 
          : 'Inter';
        const fontSizePx = fontSettings.fontSizePx || 14;
        const fontWeight = fontSettings.fontWeight || 400;
        const fontStyle = fontSettings.fontStyle || 'normal';
        
        // Resolve font UID using the font resolver (same as Canvas)
        const { fontResolver } = await import('../../services/FontResolver');
        const resolvedFontUid = fontResolver.resolveFontUidSync(fontFamily, fontWeight, fontStyle);
        
        DEBUG_ENABLED && console.log('📊 [VirtualPagesV3] Resolved font:', {
          fontFamily,
          fontSizePx,
          fontWeight,
          fontStyle,
          resolvedFontUid
        });
        
        // Convert page setup to format expected by initializeDocument
        // 🔧 FIX: Use paragraphs from AST if available, otherwise fallback to single paragraph
        // Without proper paragraph boundaries, WASM treats \n as line breaks instead of
        // recognizing \n\n as paragraph delimiters, causing incorrect rendering.
        const paragraphsForInit = (documentState as any).paragraphs?.length > 0
          ? (documentState as any).paragraphs.map((p: any) => ({
              id: p.id,
              type: p.type || 'p',
              graphemeStart: p.textStart,
              graphemeEnd: p.textEnd,
              version: p.version || 1
            }))
          : [{
              id: 'para-0',
              type: 'p',
              graphemeStart: 0,
              graphemeEnd: documentState.text.length,
              version: 1
            }];
        
        // 🔬 ALWAYS-ON DIAGNOSTIC: Track paragraph initialization to debug save/load issues
        console.log('📊 [VirtualPagesV3] Paragraphs for WASM init:', {
          count: paragraphsForInit.length,
          fromAST: !!(documentState as any).paragraphs?.length,
          astParagraphCount: (documentState as any).paragraphs?.length || 0,
          boundaries: paragraphsForInit.map((p: any) => `[${p.graphemeStart}, ${p.graphemeEnd})`),
          paragraphDetails: paragraphsForInit.map((p: any) => ({
            id: p.id,
            start: p.graphemeStart,
            end: p.graphemeEnd,
            isEmpty: p.graphemeStart === p.graphemeEnd
          })),
          textLength: documentState.text.length,
          textPreview: documentState.text.substring(0, 80),
          newlinePositions: [...documentState.text.matchAll(/\n/g)].map(m => m.index)
        });
        
        const initInput = {
          text: documentState.text,
          paragraphs: paragraphsForInit,
          pageSetup: wasmCompatiblePageSetup,
          fonts: [{ 
            fontUid: resolvedFontUid,
            family: fontFamily, 
            weight: fontWeight, 
            style: fontStyle 
          }],
          defaultFontSizePx: fontSizePx
        };
        
        DEBUG_ENABLED && console.log('📊 [VirtualPagesV3] Initializing document...', {
          textLength: documentState.text.length,
          pageSetupFormat: 'converted',
          hasCustomPageSetup: !!documentState.pageSetup,
          fontFamily,
          fontSizePx,
          fontWeight,
          fontStyle,
          isEmptyToNonEmpty,
          fontSettingsChanged,
          forceReinit: isEmptyToNonEmpty || fontSettingsChanged
        });
        
        // ✅ FIX: Force re-initialization when transitioning from empty to non-empty OR when font settings change
        const forceReinit = isEmptyToNonEmpty || fontSettingsChanged;
        await sharedWasmClientRef.current.initializeDocument(initInput, forceReinit);
        
        DEBUG_ENABLED && console.log('✅ [VirtualPagesV3] Document initialized, now paginating...');
        
        // 🔧 CRITICAL FIX: After initializeDocument, call layoutAffectedParagraphs for ALL paragraphs
        // This populates cachedParagraphSnapshots with per-paragraph data.
        // Without this, getVisiblePages only stores ONE combined snapshot (para-0),
        // which causes duplicate line ranges when rendering multi-paragraph documents.
        if (paragraphsForInit.length > 0) {
          const paragraphIds = paragraphsForInit.map((p: any) => p.id);
          console.log('📊 [VirtualPagesV3] Calling layoutAffectedParagraphs for initial paragraph snapshots:', {
            paragraphCount: paragraphIds.length,
            paragraphIds
          });
          
          try {
            const layoutResult = await sharedWasmClientRef.current.layoutAffectedParagraphs(paragraphIds);
            console.log('✅ [VirtualPagesV3] Initial paragraph layout complete:', {
              layoutsCount: layoutResult.layouts?.length || 0,
              hasErrors: layoutResult.hasErrors,
              unknownParagraphIds: layoutResult.unknownParagraphIds
            });
            
            // 🔧 CRITICAL: Store the per-paragraph snapshots in the cache
            // This is what enables correct line extraction by paragraph slice
            if (layoutResult.layouts && layoutResult.layouts.length > 0) {
              sharedWasmClientRef.current.storeInitialParagraphSnapshots(layoutResult.layouts);
            }
          } catch (layoutError) {
            console.error('❌ [VirtualPagesV3] Initial layoutAffectedParagraphs failed:', layoutError);
            // Continue with pagination - the combined snapshot from getVisiblePages will be used as fallback
          }
        }
        
        // 🆕 Step 1.5: Check persistent cache before typesetting
        // If pages are cached, we can skip expensive WASM typesetting
        // 🔧 FIX: Use server-provided astHash (authoritative) instead of computing client-side
        let cacheHit = false;  // Track cache hit locally (React state is async)
        let usedContentHash: string | undefined;  // Track hash for saving later
        if (docId && cacheStatus.enabled) {
          setCacheStatus(prev => ({ ...prev, loading: true }));
          
          try {
            // Use server-provided astHash if available, otherwise compute client-side
            // Server astHash is authoritative and matches exactly what's in Postgres
            if (astHash) {
              // Use first 16 chars of SHA-256 for reasonable key length
              usedContentHash = astHash.substring(0, 16);
              console.log(`💾 [VirtualPagesV3] Using server astHash for cache: ${usedContentHash}...`);
            } else {
              // Fallback: compute client-side (for new documents without server hash)
              usedContentHash = await sharedWasmClientRef.current.computeContentHash(
                documentState.text,
                fontFamily,
                fontWeight,
                fontStyle
              );
              console.log(`💾 [VirtualPagesV3] Computed client hash (no server astHash): ${usedContentHash}`);
            }
            
            const cacheCheck = await sharedWasmClientRef.current.checkPersistentCache(usedContentHash);
            console.log(`💾 [VirtualPagesV3] Cache check result:`, cacheCheck);
            
            // Load from cache if we have ANY pages cached (even just 1)
            if (cacheCheck.hasCache && cacheCheck.pageCount > 0) {
              // Load from cache instead of typesetting
              const visiblePages = [0, 1, 2, 3, 4]; // Initial visible pages
              const cached = await sharedWasmClientRef.current.loadFromPersistentCache(visiblePages);
              
              if (cached.misses.length === 0) {
                console.log(`⚡ [VirtualPagesV3] Loaded ${cached.hits} pages from cache (skipped typesetting)`);
                cacheHit = true;  // Mark as cache hit
                setCacheStatus(prev => ({
                  ...prev,
                  loading: false,
                  source: 'cache',
                  hitCount: prev.hitCount + cached.hits
                }));
                
                // TODO: Build pagination result from cached pages
                // For now, we still need to typeset to get the full result structure
                // In a future optimization, we could construct result from cache alone
              }
            }
          } catch (cacheError) {
            console.warn('⚠️ [VirtualPagesV3] Cache check failed, falling back to typesetting:', cacheError);
          }
          
          setCacheStatus(prev => ({ ...prev, loading: false }));
        }
        
        // Step 2: Request ALL pages at once (0 to 999)
        // CRITICAL: Must pass font parameters to avoid defaulting to "Times New Roman"
        // Convert page setup to WASM format for getVisiblePages call
        const wasmPageSetup = documentState.pageSetup
          ? PageSetupConverter.toWasmFormat(documentState.pageSetup)
          : PAGE_SIZES.LETTER;

        const result = await sharedWasmClientRef.current.getVisiblePages(
          documentState.text,
          wasmPageSetup,
          [0, 999], // Request all pages
          fontFamily, // Font family from document
          fontSizePx, // Font size from document
          0, // No lookahead
          fontWeight, // Use actual font weight from document
          fontStyle // Use actual font style from document
        );
        
        DEBUG_ENABLED && console.log(`✅ [VirtualPagesV3] Pagination complete: ${result.total_pages} pages`, {
          hasPages: !!result.pages,
          pagesCount: result.pages?.length || 0
        });
        
        setPaginationResult(result);
        setActualPageCount(result.total_pages);
        onPageCountChange?.(result.total_pages);
        
        // 🆕 Save typeset pages to persistent cache (IndexedDB)
        // Only save if this wasn't already a cache hit
        // Pass the same contentHash we used for lookup to ensure consistency
        if (cacheStatus.enabled && !cacheHit && result.pages && result.pages.length > 0) {
          try {
            await sharedWasmClientRef.current.saveToPersistentCache(result.pages, usedContentHash);
            console.log(`💾 [VirtualPagesV3] Saved ${result.pages.length} pages to persistent cache (hash: ${usedContentHash?.substring(0, 8)}...)`);
          } catch (saveError) {
            console.warn('⚠️ [VirtualPagesV3] Failed to save pages to cache:', saveError);
          }
        }
        
        // 🆕 Update cache status after pagination
        // Use local cacheHit variable since React state is async
        if (cacheStatus.enabled && !cacheHit) {
          setCacheStatus(prev => ({
            ...prev,
            source: 'typeset',
            missCount: prev.missCount + (result.pages?.length || 0)
          }));
        }
        
      } catch (error) {
        console.error('❌ [VirtualPagesV3] Initialization/Pagination failed:', error);
      }
    };
    
    initializeAndPaginate();
  }, [
    isSharedWorkerReady,
    documentState?.pageSetup,
    // ❌ REMOVED: documentState?.text - causes full re-init on every keystroke
    // ✅ FONT SWITCHING FIX: Use individual font settings properties for stable dependency comparison
    documentState?.fontSettings?.fontFamily,
    documentState?.fontSettings?.fontWeight,
    documentState?.fontSettings?.fontStyle,
    documentState?.fontSettings?.fontSizePx,
    documentState?.fontSettings?.lineHeightMul,
    onPageCountChange,
    // 🔧 FIX: Re-initialize when paragraphs are loaded from AST
    // This is needed for document load - paragraphs arrive after initial render
    // Using length as dependency to avoid re-init on every keystroke (paragraph objects change)
    (documentState as any)?.paragraphs?.length
  ]);
  
  // Callback to receive page count from canvas (used for both initial and incremental updates)
  const handlePageCountChange = React.useCallback((count: number) => {
    DEBUG_ENABLED && console.log(`📄 [VirtualPagesV3] Page count updated from canvas: ${count}`);
    
    // 🔥 CRITICAL FIX: Always trust the page count from WASM/TypesetterWorkerClient
    // The previous validation logic used documentState?.text to verify page count decreases,
    // but documentState?.text is STALE during React's async state update cycle.
    // 
    // When a page break is deleted:
    // 1. SharedInputContext triggers re-render with new page count (3)
    // 2. But documentState?.text still has old text (3 form feeds instead of 2)
    // 3. The validation calculated expectedPages = 3+1 = 4, rejected count=3
    // 4. Page 3 was incorrectly re-created with stale/ghost content
    //
    // TypesetterWorkerClient.ts now correctly calculates page count using form feed count
    // from the ACTUAL new text (not stale React state), so we can trust it.
    if (count !== actualPageCount) {
      console.log(`📄 [VirtualPagesV3] Page count changing: ${actualPageCount} → ${count}`);
      setActualPageCount(count);
      onPageCountChange?.(count);
    }
  }, [onPageCountChange, actualPageCount]);
  
  // ✅ CRITICAL FIX: Memoize documentState object to prevent unnecessary re-renders
  // This prevents creating a new object reference on every render, which causes Canvas remounts
  // 
  // ⚠️ IMPORTANT: DO NOT include documentState?.text in dependencies!
  // - Text changes should NOT cause RowComponent recreation (which causes Canvas unmount)
  // - Canvas manages text internally via currentDocumentTextRef
  // - Including text here causes: memoizedDocumentState changes → RowComponent changes → Canvas remounts → cursor resets
  // 
  // Text is passed through the object for INITIAL LOAD ONLY. Subsequent text changes are:
  // 1. Handled internally by Canvas (currentDocumentTextRef)
  // 2. Synced via the existing setupDocumentTextSync mechanism when needed
  const memoizedDocumentState = React.useMemo(() => {
    if (!documentState) return undefined;
    return {
      ...documentState,
      text: documentState.text || '', // Ensure text is always a string
      pageSetup: documentPageSetup // Pass converted page setup in documentState
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentState?.fontSettings, documentPageSetup]); // ⚠️ Intentionally excludes documentState?.text

  // Row component for react-window List
  const RowComponent = React.useCallback(({ index, style, ...rowProps }: any) => (
    <div
      style={{
        ...style,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
      }}
    >
      <EdgeDocsPageItem
        pageIndex={index}
        documentState={memoizedDocumentState}
        pageWidth={pageWidth}
        pageHeight={pageHeight}
        scale={finalScale}
        pageSetup={documentPageSetup} // Also pass as prop for compatibility
        onWasmStatusChange={index === 0 ? onWasmStatusChange : undefined}
        onDocumentTextChange={index === 0 ? onDocumentTextChange : undefined}
        onPageCountChange={index === 0 ? handlePageCountChange : undefined}
        sharedWasmClient={sharedWasmClientRef.current}
        skipInitialization={isSharedWorkerReady}
        pageData={paginationResult?.pages?.[index]}
        paginationResult={paginationResult}
      />
    </div>
  ), [memoizedDocumentState, pageWidth, pageHeight, finalScale, documentPageSetup, onWasmStatusChange, onDocumentTextChange, handlePageCountChange, isSharedWorkerReady, paginationResult]);

  // Empty row props object (required by react-window v2)
  const rowPropsData = React.useMemo(() => ({}), []);

  return (
    <>
      {/* 💾 Cache Status Indicator - Shows cache hit/miss status */}
      <CacheStatusIndicator
        enabled={cacheStatus.enabled}
        loading={cacheStatus.loading}
        source={cacheStatus.source}
        hitCount={cacheStatus.hitCount}
        missCount={cacheStatus.missCount}
        position="bottom-right"
      />
      
      <SharedInputProvider
        typesetterClient={sharedWasmClientRef.current}
        documentState={memoizedDocumentState}
        onDocumentTextChange={onDocumentTextChange}
        onPageCountChange={onPageCountChange}
      >
        {/* 🆕 Register paragraph getter with parent for save operations */}
        <ParagraphGetterRegistrar onReady={onParagraphGetterReady} />
        {/* 🆕 Register undo/redo handlers with parent for toolbar */}
        <UndoRedoRegistrar onReady={onUndoRedoReady} />
        <VirtualPagesList
          className={className}
          listRef={listRef}
          pageCount={pageCount}
          pageHeight={pageHeight}
          finalScale={finalScale}
          pageGap={pageGap}
          RowComponent={RowComponent}
          rowPropsData={rowPropsData}
        />
      </SharedInputProvider>
    </>
  );
};

// ============================================================================
// VirtualPagesList - Wrapper component for react-window List
// 
// 🔥 CRITICAL: This component does NOT subscribe to sharedRenderTrigger!
// Previously, passing sharedRenderTrigger through rowProps caused ALL Canvas
// components to remount on every keystroke, breaking typing on earlier pages.
// 
// New pages are created by changing pageCount (which changes rowCount).
// Incremental renders are handled by the Canvas Render Registry (direct callbacks).
// ============================================================================
interface VirtualPagesListProps {
  className: string;
  listRef: React.MutableRefObject<ListImperativeAPI | null>;
  pageCount: number;
  pageHeight: number;
  finalScale: number;
  pageGap: number;
  RowComponent: (props: any) => React.ReactNode;
  rowPropsData: any;
}

const VirtualPagesList: React.FC<VirtualPagesListProps> = ({
  className,
  listRef,
  pageCount,
  pageHeight,
  finalScale,
  pageGap,
  RowComponent,
  rowPropsData
}) => {
  // 🔥 CRITICAL FIX: Do NOT subscribe to sharedRenderTrigger here!
  // Previously, we passed sharedRenderTrigger through rowProps, which caused
  // ALL Canvas components to remount whenever renderTrigger changed.
  // This broke typing on pages 0-2 after creating page 3.
  //
  // The correct approach:
  // 1. New pages are created by changing pageCount (which changes rowCount)
  // 2. Incremental renders are handled by the Canvas Render Registry (direct callbacks)
  // 3. Full re-renders are only needed for structural changes (new pages, font changes)
  //
  // We still subscribe to the context to ensure the component re-renders when
  // pageCount changes (via actualPageCount state in parent), but we don't pass
  // renderTrigger to rows.
  
  DEBUG_ENABLED && console.log('📱 [VirtualPagesList] Render with pageCount:', pageCount);
  
  // 🔥 FIX: Use stable rowProps - don't include renderTrigger
  // This prevents Canvas remounts on every keystroke
  const stableRowProps = React.useMemo(() => ({
    ...rowPropsData
  }), [rowPropsData]);
  
  // 📜 SCROLL INDICATOR: Track scroll state for page indicator
  const [isScrolling, setIsScrolling] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [scrollPercentage, setScrollPercentage] = useState(0);
  const [currentScrollOffset, setCurrentScrollOffset] = useState(0);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // ⌨️ KEYBOARD SCROLLING: Page Up/Down, Ctrl+Home/End support
  useKeyboardScrolling({
    listRef,
    pageHeight,
    pageGap,
    scale: finalScale,
    pageCount,
    enabled: true,
    getCurrentScroll: () => currentScrollOffset
  });
  
  // Calculate page from scroll position
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight - target.clientHeight;
    
    // Track current scroll offset for keyboard scrolling
    setCurrentScrollOffset(scrollTop);
    
    // Calculate current page (1-indexed)
    const pageWithGap = pageHeight * finalScale + pageGap;
    const page = Math.floor(scrollTop / pageWithGap) + 1;
    setCurrentPage(Math.min(Math.max(1, page), pageCount));
    
    // Calculate scroll percentage for indicator positioning
    const percentage = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
    setScrollPercentage(percentage);
    
    // Show indicator
    setIsScrolling(true);
    
    // Hide indicator after scroll stops (with delay)
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 1200); // Hide 1.2s after scroll stops
  }, [pageHeight, finalScale, pageGap, pageCount]);
  
  // Handle scroll end event from react-window
  const handleScrollEnd = useCallback(() => {
    // Clear any pending timeout and hide indicator after a brief delay
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      setIsScrolling(false);
    }, 800); // Shorter delay on scroll end
  }, []);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);
  
  return (
    <>
      {/* 📜 Page Indicator - Shows during scroll */}
      <ScrollPageIndicator
        currentPage={currentPage}
        totalPages={pageCount}
        isScrolling={isScrolling}
        verticalPosition={scrollPercentage}
      />
      
      <div
        ref={containerRef}
        className={`edgedocs-virtual-pages-v3 ${className}`}
        style={{
          width: '100%',
          height: '100vh',
          backgroundColor: '#f0f0f0',
          paddingTop: '0px',
          paddingLeft: '0px',
          paddingRight: '0px',
          paddingBottom: '20px',
        }}
      >
        <List
          listRef={listRef}
          defaultHeight={window.innerHeight}
          rowCount={pageCount}
          rowHeight={pageHeight * finalScale + pageGap}
          rowComponent={RowComponent}
          rowProps={stableRowProps}
          overscanCount={2}
          onScroll={handleScroll}
          onScrollEnd={handleScrollEnd}
        />
      </div>
    </>
  );
};

